// Rich, AEO/SEO-optimized content for service detail pages.
// Adds depth, authority signals, conversion psychology, and citable sources
// on top of the base service definitions in services.ts.

export interface RichStat {
  value: string;
  label: string;
  source: string;
  sourceUrl?: string;
}

export interface RichStep {
  title: string;
  body: string;
}

export interface RichTimelinePhase {
  phase: string;
  duration: string;
  what: string;
}

export interface RichAlternative {
  name: string;
  whenItFits: string;
}

export interface RichMistake {
  title: string;
  body: string;
}

export interface RichCitation {
  label: string;
  url: string;
}

export interface ServiceRichContent {
  /** 2–4 short citable bullets shown next to the QuickAnswer. */
  quickAnswerBullets?: string[];
  /** Loss-aversion / urgency block placed directly under the hero. */
  urgency?: { headline: string; points: string[] };
  /** Authority statistics with citations (anchoring + social proof). */
  stats?: RichStat[];
  /** "How it actually works" — numbered, specific, IRS-form referenced. */
  howItWorks?: RichStep[];
  /** Who this is NOT for — qualifies leads, builds trust. */
  disqualifiers?: string[];
  /** Anchored expectations — small, medium, full timeline. */
  timeline?: RichTimelinePhase[];
  /** Transparency on cost drivers. */
  costFactors?: string[];
  /** Common DIY mistakes — leverages authority + loss aversion. */
  mistakes?: RichMistake[];
  /** Alternatives compared — when this fits vs. when something else does. */
  alternatives?: RichAlternative[];
  /** 3 differentiators specific to CTR for this service. */
  whyCTR?: string[];
  /** Additional Q&A appended to the base FAQs. */
  extendedFaqs?: { q: string; a: string }[];
  /** Authoritative external sources (IRS.gov, IRM, USC, Tax Court). */
  citations?: RichCitation[];
}

const defaultWhyCTR: string[] = [
  "Founder Franklin Sofi, MBA, CPA — Certified Tax Resolution Specialist with 16+ years representing taxpayers before the IRS and ASTPS Gold Member.",
  "You never speak to the IRS. Power of Attorney (Form 2848) is filed the same day so every call, letter, and Revenue Officer is routed to our office.",
  "Flat-fee engagements with a written scope. No hourly surprises, no monthly retainers stretching out a case that should close.",
];

const defaultCitations: RichCitation[] = [
  { label: "IRS Taxpayer Bill of Rights", url: "https://www.irs.gov/taxpayer-bill-of-rights" },
  { label: "Internal Revenue Manual (IRM)", url: "https://www.irs.gov/irm" },
  { label: "IRS Data Book — Collections & Enforcement", url: "https://www.irs.gov/statistics/soi-tax-stats-irs-data-book" },
];

export const serviceRichContent: Record<string, ServiceRichContent> = {
  // ───────────── TAX DEBT RELIEF ─────────────
  "offer-in-compromise": {
    quickAnswerBullets: [
      "Federal program under IRC § 7122 — settles tax debt for less than the full balance.",
      "IRS accepted roughly 12,700 of 33,000 OICs in FY 2023 (~37% acceptance rate).",
      "Average accepted offer settled $0.14 on the dollar in recent IRS data.",
    ],
    urgency: {
      headline: "Every month you wait, your offer amount goes up — and your statute shrinks.",
      points: [
        "Your offer is calculated from your future income × 12 (lump sum) or × 24 (periodic). Higher recent paychecks raise your required offer.",
        "Interest and failure-to-pay penalties keep compounding daily on the underlying liability while you decide.",
        "Filing an OIC pauses the 10-year IRS Collection Statute Expiration Date (CSED) — a strategy that helps some taxpayers and hurts others. Order matters.",
      ],
    },
    stats: [
      { value: "37%", label: "IRS OIC acceptance rate, FY 2023", source: "IRS Data Book Table 25", sourceUrl: "https://www.irs.gov/statistics/soi-tax-stats-irs-data-book" },
      { value: "$0.14", label: "Average settled per $1 owed (accepted OICs)", source: "IRS Office of Servicewide Policy data" },
      { value: "6–12 mo", label: "Typical IRS processing time", source: "IRS Form 656-B instructions", sourceUrl: "https://www.irs.gov/forms-pubs/about-form-656" },
    ],
    howItWorks: [
      { title: "Pre-qualification (RCP analysis)", body: "We compute your Reasonable Collection Potential — net realizable equity in assets plus 12 or 24 months of disposable income. If RCP exceeds the balance owed, an OIC will not be accepted and we recommend a different path before you pay an application fee." },
      { title: "Form 656 + Form 433-A (OIC) / 433-B (OIC) package", body: "We document allowable living expenses under the IRS Collection Financial Standards (housing, transportation, food, healthcare), substantiate every line, and structure the offer as lump-sum or periodic to minimize the total." },
      { title: "Submission, IRS review, and counter-offers", body: "The case is assigned to an IRS Offer Examiner. We answer every information request, defend valuations, and negotiate counter-offers. Collection activity is generally paused while the offer is under review." },
      { title: "Acceptance, appeal, or pivot", body: "Accepted offers require 5 years of perfect compliance (filing + paying) — we monitor that window. Rejected offers go to the IRS Independent Office of Appeals; if Appeals fails, we pivot to an installment agreement or CNC without losing momentum." },
    ],
    disqualifiers: [
      "You can full-pay the balance within the remaining statute through liquid assets or income.",
      "You have current-year unfiled returns or are not making required estimated payments — the IRS will return the offer unprocessed.",
      "You are in an open bankruptcy proceeding (file the OIC after discharge).",
    ],
    timeline: [
      { phase: "Investigation & RCP analysis", duration: "2–4 weeks", what: "Pull transcripts, build financials, decide if an OIC is the right tool." },
      { phase: "Preparation & submission", duration: "2–3 weeks", what: "Document expenses, build Forms 656 / 433-A (OIC), submit with the $205 fee and 20% down (lump sum)." },
      { phase: "IRS review to decision", duration: "6–12 months", what: "Examiner review, counter-offers, possible Appeals. Collections are generally paused throughout." },
    ],
    costFactors: [
      "Number of years of returns to be reconstructed or filed before submission.",
      "Complexity of your asset picture — self-employment, rentals, equity in retirement accounts.",
      "Whether your case requires an Appeals protest or a Doubt-as-to-Liability analysis.",
      "Lump-sum vs. periodic payment structure (affects required filings).",
    ],
    mistakes: [
      { title: "Submitting before pulling transcripts", body: "Half of OIC rejections come from undisclosed liabilities or missed returns. We pull a full IRS account transcript first — every time." },
      { title: "Overstating expenses outside IRS standards", body: "Examiners reject expenses that exceed the Collection Financial Standards without substantiation. Documentation wins; assertions don't." },
      { title: "Ignoring dissipated assets", body: "If you transferred assets in the last 3 years, the IRS adds them back to your RCP. We address this proactively in the offer narrative." },
      { title: "Defaulting the 5-year compliance period", body: "An accepted OIC reverts and the original balance is reinstated if you miss a filing or payment in the next 5 years. We build a compliance calendar at acceptance." },
    ],
    alternatives: [
      { name: "Partial Pay Installment Agreement (PPIA)", whenItFits: "Your RCP is too high for an OIC but you can't full-pay within the statute — pay reduced monthly amounts until the CSED runs." },
      { name: "Currently Not Collectible (CNC)", whenItFits: "Income covers only basic living expenses today, but is expected to recover. Pauses collections without committing to a settlement." },
      { name: "Bankruptcy (Chapter 7 or 13)", whenItFits: "Older income-tax debt that meets the 3-year, 2-year, 240-day rules may be dischargeable — coordinated with bankruptcy counsel." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "How much does an Offer in Compromise cost out of pocket?", a: "The IRS application fee is $205 (waived if you meet the low-income certification). Lump-sum offers require 20% down with the application. Professional fees vary by case complexity — we quote a flat fee after the free consultation." },
      { q: "Will an OIC remove a federal tax lien?", a: "Filing the offer does not remove the lien. Once the offer is paid in full and the 5-year compliance window starts, the lien is released. We can request a lien withdrawal under qualifying conditions." },
      { q: "Can I submit an OIC while in an installment agreement?", a: "Yes. The installment agreement is suspended during OIC review. If the OIC is rejected, the installment agreement resumes." },
      { q: "Does an OIC affect my credit score?", a: "The OIC itself is not reported to credit bureaus. The underlying federal tax lien (if filed) does appear on title and asset searches." },
    ],
    citations: [
      { label: "IRC § 7122 — Compromises", url: "https://www.law.cornell.edu/uscode/text/26/7122" },
      { label: "IRS Form 656-B — Offer in Compromise Booklet", url: "https://www.irs.gov/forms-pubs/about-form-656" },
      { label: "IRS Collection Financial Standards", url: "https://www.irs.gov/businesses/small-businesses-self-employed/collection-financial-standards" },
      { label: "IRM 5.8 — Offer in Compromise", url: "https://www.irs.gov/irm/part5/irm_05-008-001" },
    ],
  },

  "installment-agreement": {
    quickAnswerBullets: [
      "Five IRS plan types: guaranteed, streamlined, non-streamlined, partial-pay (PPIA), in-business trust fund express.",
      "Balances under $50,000 typically qualify for a streamlined plan with no financial disclosure required.",
      "An accepted plan stops new levies and lifts active wage garnishments.",
    ],
    urgency: {
      headline: "An IA filed today can release a wage garnishment within 24–72 hours.",
      points: [
        "Once the IRS issues Letter 1058 / LT11, you have 30 days before levies can begin — every day past that is lost protection.",
        "Streamlined plans avoid Form 433 financial disclosure entirely. Wait too long and your balance crosses thresholds that force full financials.",
        "Setup fees range from $31 (direct debit online) to $225 (manual). Procrastination costs real money even before interest.",
      ],
    },
    stats: [
      { value: "3.1M+", label: "Installment agreements active with the IRS", source: "IRS Data Book FY 2023" },
      { value: "$50K", label: "Streamlined IA threshold — no financials required", source: "IRM 5.14.5", sourceUrl: "https://www.irs.gov/irm/part5/irm_05-014-005" },
      { value: "72 mo", label: "Maximum standard streamlined term", source: "IRS.gov" },
    ],
    howItWorks: [
      { title: "Right-size the plan to your balance", body: "Under $10K → guaranteed. Under $50K → streamlined (72-month max). Over $50K → non-streamlined with Form 433-F or 433-A. Truly unaffordable → Partial Pay IA (PPIA) requires Form 433 and 2-year reviews." },
      { title: "Form 9465 + supporting financials", body: "We file Form 9465 (Installment Agreement Request), and for higher balances Form 433-F or 433-A to document income, expenses, and equity. We negotiate using the IRS Collection Financial Standards." },
      { title: "Levy release & lien work", body: "Once the IA is accepted, we request release of active wage and bank levies and — for direct-debit IAs under $25K — apply to withdraw the Notice of Federal Tax Lien from public record." },
      { title: "Stay-compliant monitoring", body: "Default is the #1 risk: a single missed filing or estimated payment terminates the agreement and reinstates collections. We build a compliance calendar and review quarterly." },
    ],
    disqualifiers: [
      "You have the liquidity to full-pay within 120 days — request a Short-Term Payment Plan instead (no setup fee).",
      "Your monthly disposable income is zero or negative — pursue Currently Not Collectible (CNC) status instead.",
      "Your equity in assets plus future income exceeds the balance — you may qualify for an OIC instead.",
    ],
    timeline: [
      { phase: "Engagement & transcript pull", duration: "1–3 days", what: "Power of Attorney filed same day; full IRS account transcript ordered." },
      { phase: "Plan structuring & filing", duration: "1–2 weeks", what: "Choose plan type, build financials if required, file Form 9465 and supporting documentation." },
      { phase: "IRS approval & levy release", duration: "2–8 weeks", what: "Streamlined plans approve fast; non-streamlined plans require examiner review. Levies released on acceptance." },
    ],
    costFactors: [
      "IRS setup fee ($31–$225 depending on application method and direct-debit election).",
      "Whether full financial disclosure (Form 433-F or 433-A) is required.",
      "Whether an active levy needs emergency release work alongside the IA.",
      "Lien withdrawal application work, if applicable.",
    ],
    mistakes: [
      { title: "Defaulting to the highest payment the IRS proposes", body: "The IRS will accept the lowest payment supported by your financials. Without representation, most taxpayers agree to amounts they can't sustain — and default within a year." },
      { title: "Skipping the lien withdrawal application", body: "Direct-debit IAs under $25K qualify for Form 12277 lien withdrawal. Most taxpayers never apply — and the lien quietly destroys their credit for years." },
      { title: "Forgetting to make estimated payments", body: "If you're self-employed, missing a quarterly estimate is an automatic IA default. We build this into the plan from day one." },
      { title: "Agreeing to a plan before pulling transcripts", body: "Without transcripts, you don't know if there's an unfiled return, additional balance, or pending exam that will blow up the IA the moment it's signed." },
    ],
    alternatives: [
      { name: "Offer in Compromise", whenItFits: "Future income × 12 plus net realizable equity is less than what you owe." },
      { name: "Currently Not Collectible", whenItFits: "You can't even afford a streamlined minimum payment — pause everything." },
      { name: "Short-Term Payment Plan (≤120 days)", whenItFits: "You can clear the balance within 4 months — no setup fee and no formal IA needed." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "Will an installment agreement stop a wage garnishment already in place?", a: "Yes — once the IA is accepted, the IRS releases the active levy. Most releases land at the employer within 24–72 hours of acceptance." },
      { q: "What's the lowest monthly payment the IRS will accept?", a: "There is no fixed minimum. The IRS uses your Form 433 financials and Collection Financial Standards to compute disposable income. For streamlined IAs under $50K, you can divide the balance by 72 months." },
      { q: "Can I deduct interest paid on an IRS installment agreement?", a: "No — interest paid on personal federal income tax is not deductible. Business tax interest may be deductible." },
      { q: "What happens if I default?", a: "The IRS sends Notice CP523 — 30 days to cure before termination. After termination, collections resume immediately and new levies can issue." },
    ],
    citations: [
      { label: "IRC § 6159 — Agreements for payment of tax liability in installments", url: "https://www.law.cornell.edu/uscode/text/26/6159" },
      { label: "IRS Form 9465 — Installment Agreement Request", url: "https://www.irs.gov/forms-pubs/about-form-9465" },
      { label: "IRM 5.14 — Installment Agreements", url: "https://www.irs.gov/irm/part5/irm_05-014-001" },
    ],
  },

  "currently-not-collectible": {
    quickAnswerBullets: [
      "IRS hardship status under IRM 5.16 — collections stop until your income recovers.",
      "Requires Form 433-F or 433-A showing disposable income at or below IRS allowable expenses.",
      "The 10-year Collection Statute (CSED) continues running while you're in CNC.",
    ],
    urgency: {
      headline: "CNC is the only IRS status that stops collections without requiring you to pay anything.",
      points: [
        "Pending wage levies and bank levies can be released the day CNC is granted.",
        "Every month you stay in CNC, the 10-year statute burns down — debt that was 4 years old becomes 5 with no payment made.",
        "Delay risks an active levy hitting before you submit financials — far harder to unwind than to prevent.",
      ],
    },
    stats: [
      { value: "10 yr", label: "IRS Collection Statute (CSED) — runs while in CNC", source: "IRC § 6502" },
      { value: "433-F", label: "Most common financial form used to qualify", source: "IRS.gov" },
      { value: "$84K", label: "Median CNC income threshold for a family of 4 (varies)", source: "IRS Collection Financial Standards" },
    ],
    howItWorks: [
      { title: "Financial disclosure (Form 433-F or 433-A)", body: "We document monthly income vs. necessary living expenses under the IRS Collection Financial Standards. If allowable expenses meet or exceed income, you qualify." },
      { title: "Submission to ACS or your Revenue Officer", body: "Most cases run through Automated Collection System (ACS); high-balance or assigned cases go to a Revenue Officer. We negotiate placement based on your facts." },
      { title: "Levy release & status confirmation", body: "Once CNC is granted, we get written confirmation, release active levies, and document the trigger income above which the IRS will reopen collections." },
      { title: "Statute monitoring", body: "We track each tax-year CSED. For older debt, CNC can mean the IRS never collects — the liability simply expires." },
    ],
    disqualifiers: [
      "You have liquid assets or equity that could be tapped to pay the balance.",
      "Your income is comfortably above IRS allowable living expenses for your household.",
      "You have unfiled returns — file them first; CNC requires current compliance.",
    ],
    timeline: [
      { phase: "Document gathering", duration: "1–2 weeks", what: "Pay stubs, bank statements, expense substantiation, asset summary." },
      { phase: "Form 433 prep & submission", duration: "1 week", what: "Build the financial picture; submit to ACS or Revenue Officer." },
      { phase: "IRS decision & levy release", duration: "2–8 weeks", what: "ACS often grants quickly; Revenue Officer cases take longer." },
    ],
    costFactors: [
      "Whether you also need unfiled returns prepared before CNC can be requested.",
      "ACS vs. assigned Revenue Officer (RO cases require more negotiation).",
      "Number of tax years and asset complexity to document.",
    ],
    mistakes: [
      { title: "Listing expenses that exceed IRS standards without substantiation", body: "The IRS replaces over-standard amounts with the local standard — sinking your case. We use the standard or document the exception in advance." },
      { title: "Forgetting that CNC is reviewed annually", body: "The IRS pulls your tax return each year. A bump in income reopens collections. We monitor and prepare proactively." },
      { title: "Skipping the lien-withdrawal angle", body: "CNC alone doesn't release a lien. Combined with full-pay through statute expiration, it can — but requires planning." },
    ],
    alternatives: [
      { name: "Offer in Compromise", whenItFits: "Future income is unlikely to recover meaningfully — settle and close the book." },
      { name: "Partial Pay Installment Agreement", whenItFits: "You can afford something small and prefer active resolution over indefinite hardship status." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "Does CNC stop interest and penalties?", a: "No — they continue to accrue. CNC stops active collection (levies, garnishments, calls), not the underlying liability growth." },
      { q: "Can the IRS take my tax refund while I'm in CNC?", a: "Yes — refunds are offset to the balance even during CNC. That offset can be advantageous because it pays down the debt without affecting cash flow." },
      { q: "How long can I stay in CNC?", a: "Indefinitely, as long as financial hardship continues. Many cases stay in CNC until the 10-year CSED expires and the debt is permanently uncollectible." },
    ],
    citations: [
      { label: "IRM 5.16 — Currently Not Collectible", url: "https://www.irs.gov/irm/part5/irm_05-016-001" },
      { label: "IRC § 6502 — Collection after assessment", url: "https://www.law.cornell.edu/uscode/text/26/6502" },
      { label: "IRS Collection Financial Standards", url: "https://www.irs.gov/businesses/small-businesses-self-employed/collection-financial-standards" },
    ],
  },

  "penalty-abatement": {
    quickAnswerBullets: [
      "Two main routes: First-Time Abatement (FTA) administrative waiver and Reasonable Cause relief.",
      "Failure-to-file (5%/mo, capped at 25%) and failure-to-pay (0.5%/mo) penalties are the most commonly abated.",
      "Once the penalty is abated, the associated interest is also abated.",
    ],
    urgency: {
      headline: "The IRS does not volunteer FTA — you have to request it, and it expires.",
      points: [
        "FTA requires a clean 3-year compliance history. Each new penalty year you accrue can disqualify you from using FTA on the older years.",
        "Refund claims for paid penalties are barred after 3 years from filing or 2 years from payment — ignored letters become permanent.",
        "Penalty stacking compounds: a single late-filed year can carry 47.5% in combined failure-to-file, failure-to-pay, and accuracy penalties.",
      ],
    },
    stats: [
      { value: "25%", label: "Maximum failure-to-file penalty (5%/mo)", source: "IRC § 6651(a)(1)" },
      { value: "$28.8B", label: "Civil penalties assessed by IRS in FY 2023", source: "IRS Data Book Table 26" },
      { value: "3 yr", label: "Clean compliance window required for FTA", source: "IRM 20.1.1.3.6" },
    ],
    howItWorks: [
      { title: "Pull transcripts and inventory every penalty", body: "We order a full IRS Account Transcript for each year. Penalties show as transaction codes (TC 166, 270, 240) with assessed amounts and dates — we list every one." },
      { title: "Test FTA eligibility year-by-year", body: "FTA waives failure-to-file, failure-to-pay, and failure-to-deposit penalties if the three prior years have no significant penalties. We apply FTA to the earliest qualifying year — usually the largest savings." },
      { title: "Build the Reasonable Cause case", body: "For penalties FTA can't reach, we document reasonable cause: serious illness, death in family, casualty/disaster, reliance on a tax professional, records destroyed. Each requires specific contemporaneous evidence." },
      { title: "Submit and appeal", body: "Most abatement requests go on Form 843 with a written narrative. Denials are appealed to the IRS Independent Office of Appeals — many denials reverse there." },
    ],
    disqualifiers: [
      "You have significant unresolved penalties in each of the past 3 years (blocks FTA).",
      "Your only justification is 'I forgot' or 'I couldn't afford to pay' — neither qualifies as reasonable cause.",
      "The penalty is fraud-related (IRC § 6663) — abatement is not available.",
    ],
    timeline: [
      { phase: "Transcript & analysis", duration: "1–2 weeks", what: "Identify every abatable penalty and the strongest argument for each year." },
      { phase: "Request submission", duration: "1–2 weeks", what: "FTA requests via phone (fast) or Form 843 with reasonable-cause narrative." },
      { phase: "IRS decision / Appeals", duration: "2–6 months", what: "FTA often grants in days; reasonable-cause cases take longer; Appeals adds 3–6 months." },
    ],
    costFactors: [
      "Number of tax years with penalties to address.",
      "Whether reasonable-cause documentation must be reconstructed (medical records, disaster declarations).",
      "Whether the case requires Appeals after initial denial.",
    ],
    mistakes: [
      { title: "Asking for reasonable cause when FTA is available", body: "FTA is administrative and granted on a phone call. Many practitioners burn months on reasonable-cause letters when FTA would have worked the same day." },
      { title: "Using FTA on a small-penalty year first", body: "FTA is a one-shot tool per compliance window. We apply it to the largest qualifying penalty for maximum savings." },
      { title: "No contemporaneous evidence", body: "Reasonable cause requires records dated to the period in question — not affidavits written after the fact." },
    ],
    alternatives: [
      { name: "Penalty appeal under hazards of litigation", whenItFits: "Appeals officer can weigh litigation risk; useful when reasonable cause is borderline." },
      { name: "Refund claim (Form 843) after payment", whenItFits: "Penalty already paid — recover via refund claim within 3-year/2-year window." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "Can I get penalty abatement if I still owe the tax?", a: "Yes — the penalty is separate from the tax. Abatement removes the penalty even if the underlying balance remains." },
      { q: "Does FTA apply to estate tax or trust fund penalties?", a: "No — FTA covers failure-to-file, failure-to-pay, and failure-to-deposit on personal and business returns. It does not cover the Trust Fund Recovery Penalty or estate/gift returns." },
      { q: "How long does FTA take?", a: "When the request is made by phone with the taxpayer's authorized representative, FTA can be granted the same day. Written requests take 2–4 months." },
    ],
    citations: [
      { label: "IRC § 6651 — Failure to file & pay penalties", url: "https://www.law.cornell.edu/uscode/text/26/6651" },
      { label: "IRM 20.1.1 — Penalty Handbook", url: "https://www.irs.gov/irm/part20/irm_20-001-001" },
      { label: "IRS Form 843 — Claim for Refund and Request for Abatement", url: "https://www.irs.gov/forms-pubs/about-form-843" },
    ],
  },

  "innocent-spouse-relief": {
    quickAnswerBullets: [
      "Three relief types under IRC § 6015: traditional, separation of liability, equitable.",
      "Form 8857 starts the process — generally must be filed within 2 years of first collection action (equitable relief is more flexible).",
      "Relief is available even after divorce, and the IRS must notify the non-requesting spouse.",
    ],
    urgency: {
      headline: "The 2-year clock on traditional and separation-of-liability relief is unforgiving.",
      points: [
        "If the IRS has already started collection (CP504, lien, levy), the 2-year deadline may already be running.",
        "Each year you delay, the IRS may pursue more aggressive enforcement against you personally.",
        "Equitable relief has no fixed deadline but the IRS gives more weight to timely filings.",
      ],
    },
    stats: [
      { value: "3", label: "Distinct relief categories under IRC § 6015", source: "IRC § 6015" },
      { value: "2 yr", label: "Deadline for traditional / separation-of-liability relief", source: "IRC § 6015(b)(1)(E)" },
      { value: "Form 8857", label: "Request for Innocent Spouse Relief", source: "IRS.gov" },
    ],
    howItWorks: [
      { title: "Determine which type of relief fits", body: "Traditional § 6015(b): you didn't know about the understatement. Separation of liability § 6015(c): divorced/separated, allocate items. Equitable § 6015(f): catch-all when (b) and (c) don't fit but enforcement would be unfair." },
      { title: "Build the factual record", body: "Marital status, financial control, abuse history, education, business involvement, awareness of the items — each factor in Rev. Proc. 2013-34 is documented." },
      { title: "Form 8857 + supporting documentation", body: "We prepare the form, attach a detailed narrative, and submit. The IRS must notify your current/former spouse, who can submit information opposing the request." },
      { title: "Examination, appeal, Tax Court", body: "Cincinnati Centralized Innocent Spouse Operation reviews. Denials are appealed to the IRS Independent Office of Appeals and, if needed, to United States Tax Court under § 6015(e)." },
    ],
    disqualifiers: [
      "You filed Married Filing Separately — innocent spouse relief is a joint-return remedy.",
      "You signed the return knowing the items were wrong and benefited from them.",
      "You and your spouse transferred assets to defraud creditors including the IRS.",
    ],
    timeline: [
      { phase: "Eligibility analysis", duration: "2–4 weeks", what: "Document marriage timeline, financial control, awareness — choose the right § 6015 prong." },
      { phase: "Form 8857 submission", duration: "1–2 weeks", what: "Detailed factual narrative + supporting documents." },
      { phase: "IRS review", duration: "6–12 months", what: "Cincinnati office review, notification to other spouse, possible Appeals." },
    ],
    costFactors: [
      "Volume of historical financial records to reconstruct.",
      "Whether domestic abuse history requires sensitive evidence handling.",
      "Whether the case proceeds to Appeals or Tax Court.",
    ],
    mistakes: [
      { title: "Filing under the wrong § 6015 prong", body: "Each prong has different requirements. Filing under (b) when (c) is the better fit risks a denial that's hard to undo." },
      { title: "Skipping the abuse / financial-control narrative", body: "Even with imperfect facts, documented abuse or financial control dramatically improves equitable relief outcomes — but only if affirmatively raised." },
      { title: "Missing the 2-year deadline by misreading collection notices", body: "The clock starts at the first collection activity against the requesting spouse. We pull transcripts to pin down the exact trigger date." },
    ],
    alternatives: [
      { name: "Injured spouse allocation (Form 8379)", whenItFits: "Your refund was taken to pay your spouse's separate debt — not the same as innocent spouse, but often confused." },
      { name: "Offer in Compromise (Doubt as to Liability)", whenItFits: "You believe the underlying tax is wrong — challenge the liability instead of just allocating it." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "Can I get relief if we're still married?", a: "Yes. § 6015(b) and (f) are available regardless of marital status. § 6015(c) requires divorce, legal separation, or no longer living together for at least 12 months." },
      { q: "Does the IRS contact my abuser?", a: "The IRS must notify the non-requesting spouse, but special procedures exist for abuse cases to limit information disclosed. We flag abuse history at submission." },
      { q: "If denied, can I refile?", a: "Equitable relief denials can be reconsidered with new facts. Tax Court provides independent review under § 6015(e)." },
    ],
    citations: [
      { label: "IRC § 6015 — Relief from joint and several liability", url: "https://www.law.cornell.edu/uscode/text/26/6015" },
      { label: "IRS Form 8857 — Request for Innocent Spouse Relief", url: "https://www.irs.gov/forms-pubs/about-form-8857" },
      { label: "Rev. Proc. 2013-34 — Equitable Relief Factors", url: "https://www.irs.gov/pub/irs-drop/rp-13-34.pdf" },
    ],
  },

  "back-taxes": {
    quickAnswerBullets: [
      "IRS Policy Statement 5-133 generally requires the last 6 years to be filed for compliance.",
      "The 10-year collection statute does not start until a return is filed (or the IRS files a Substitute for Return).",
      "Unfiled returns are a misdemeanor under IRC § 7203 — but voluntary filing closes that exposure.",
    ],
    urgency: {
      headline: "Every unfiled year is an open IRS file with no statute protection.",
      points: [
        "If the IRS files a Substitute for Return (SFR), they give you zero deductions, single filing status, and one exemption — the assessed balance is almost always inflated.",
        "Once an SFR is assessed, collections begin and the 10-year CSED starts — but on an inflated number.",
        "Refunds older than 3 years are permanently lost — every month you wait, refund years drop off.",
      ],
    },
    stats: [
      { value: "6 yr", label: "IRS compliance lookback per Policy Statement 5-133", source: "IRM 1.2.1.6.18" },
      { value: "$1.5B+", label: "Estimated unclaimed refunds expiring annually", source: "IRS news releases" },
      { value: "10 yr", label: "CSED starts only when a return is filed or SFR assessed", source: "IRC § 6502" },
    ],
    howItWorks: [
      { title: "Wage & Income transcripts for every missing year", body: "We pull IRS Wage & Income transcripts (W-2, 1099, 1098, K-1) for each unfiled year — the same data the IRS has — so reconstructed returns match third-party reporting." },
      { title: "Reconstruct deductions & credits", body: "Self-employed clients lose the most to SFRs. We reconstruct Schedule C, vehicle, home-office, and depreciation from bank statements, calendars, and contemporaneous records." },
      { title: "File in the right order", body: "Generally newest-to-oldest, but with strategy when SFRs already exist (file 1040X to override) or when refund years are at risk." },
      { title: "Resolution of the resulting balance", body: "Once compliant, we pivot to the right resolution: installment agreement, OIC, CNC, penalty abatement — often combined." },
    ],
    disqualifiers: [
      "Returns flagged for criminal investigation by IRS-CI — coordinate with a tax attorney first.",
      "Fraudulent returns (concealment of income) — voluntary disclosure analysis required before filing.",
    ],
    timeline: [
      { phase: "Transcript pull + scope", duration: "1–2 weeks", what: "Identify every unfiled year, every SFR, every existing balance." },
      { phase: "Return preparation", duration: "2–8 weeks", what: "Depending on years and records — self-employed cases take longer." },
      { phase: "Filing & resolution setup", duration: "4–12 weeks", what: "Returns processed; resolution (IA / OIC / CNC) put in place." },
    ],
    costFactors: [
      "Number of years to prepare.",
      "Whether you have records or returns must be fully reconstructed from transcripts.",
      "Business returns (Schedule C, 1120-S, 1065) cost more than W-2-only returns.",
      "Whether SFR overrides (1040X) are required.",
    ],
    mistakes: [
      { title: "Filing one year and waiting", body: "Partial filings trigger CP59 / CP63 letters that escalate the unfiled years. We file the full compliance window together when possible." },
      { title: "Letting the SFR stand", body: "An SFR can be overridden with an original return showing actual deductions — but it takes affirmative filing, not waiting." },
      { title: "Missing the 3-year refund window", body: "We file refund-year returns first when months matter. Once 3 years past the original due date, that refund is gone forever." },
    ],
    alternatives: [
      { name: "Voluntary Disclosure Program (VDP)", whenItFits: "Income was intentionally concealed (offshore accounts, unreported cash) — coordinated criminal-tax pathway." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "How far back do I have to file?", a: "IRS Policy Statement 5-133: typically the last 6 years for compliance. Your facts may require more (Revenue Officer cases) or fewer (refund-only years with no liability)." },
      { q: "Can I go to jail for unfiled returns?", a: "Failure to file is a misdemeanor under IRC § 7203, but prosecution is rare for taxpayers who voluntarily file before the IRS opens an investigation." },
      { q: "What if I don't have any records?", a: "We rebuild from IRS Wage & Income transcripts plus your bank and credit card statements. Most cases produce defensible returns even without original records." },
    ],
    citations: [
      { label: "IRS Policy Statement 5-133 (IRM 1.2.1.6.18)", url: "https://www.irs.gov/irm/part1/irm_01-002-001" },
      { label: "IRC § 7203 — Willful failure to file", url: "https://www.law.cornell.edu/uscode/text/26/7203" },
      { label: "IRS — Get Transcript", url: "https://www.irs.gov/individuals/get-transcript" },
    ],
  },

  // ───────────── IRS COLLECTIONS DEFENSE ─────────────
  "wage-garnishment": {
    quickAnswerBullets: [
      "IRS wage levies issue on Form 668-W and continue until released — not a one-time take.",
      "Exemption table (Pub. 1494) leaves very little: a single filer can keep as little as ~$1,100/month.",
      "Most levies are released within 24–72 hours of a resolution being put in place.",
    ],
    urgency: {
      headline: "Every paycheck after Form 668-W issues is partially gone — until release.",
      points: [
        "Unlike most garnishments, the IRS wage levy is continuous: every paycheck is reduced until the balance is paid or the levy is released.",
        "Employers must comply or face personal liability under IRC § 6332(d) — they will not delay for you.",
        "Levy release requires an active resolution (IA, OIC, CNC). 'I'll pay later' is not enough.",
      ],
    },
    stats: [
      { value: "668-W", label: "IRS form that creates a wage levy", source: "IRS.gov" },
      { value: "~$1,100", label: "Approx. monthly exemption — single, no dependents (Pub. 1494)", source: "IRS Publication 1494" },
      { value: "24–72 hr", label: "Typical levy release timeline once resolution in place", source: "CTR case experience" },
    ],
    howItWorks: [
      { title: "Same-day Form 2848 (Power of Attorney)", body: "We file POA the moment you call. This redirects IRS communication to our office and lets us speak to Collections, ACS, or your assigned Revenue Officer immediately." },
      { title: "Hardship release or resolution proposal", body: "If the levy is causing immediate inability to pay basic living expenses, we file a hardship request and demand release. Otherwise we put a structured resolution in front of the IRS — IA, CNC, or pending OIC." },
      { title: "Release confirmation to employer", body: "The IRS issues Form 668-D (Release of Levy) to your employer. We confirm receipt and verify your next paycheck before closing the emergency phase." },
      { title: "Long-term resolution", body: "Release is temporary protection — the underlying balance still needs resolution. We move directly into the full IA / OIC / CNC strategy." },
    ],
    disqualifiers: [
      "You have no IRS notice and no balance — verify before paying for an emergency engagement.",
      "The garnishment is from a state, child support, or private creditor — those follow different rules and we will route you to the correct help.",
    ],
    timeline: [
      { phase: "Same day", duration: "0–24 hr", what: "POA filed; IRS contacted; hardship or proposal initiated." },
      { phase: "Release", duration: "24–72 hr", what: "Form 668-D issued to employer; next paycheck restored." },
      { phase: "Resolution", duration: "30–90 days", what: "Long-term IA, OIC, or CNC put in place to prevent re-levy." },
    ],
    costFactors: [
      "Whether a Revenue Officer is assigned (RO cases are more negotiation-intensive).",
      "Whether unfiled returns must be prepared before the IRS will accept a resolution.",
      "Complexity of the long-term resolution chosen.",
    ],
    mistakes: [
      { title: "Calling the IRS yourself before engaging representation", body: "Anything you say goes in the file. Most taxpayers volunteer information that hardens the IRS position." },
      { title: "Negotiating a resolution you can't sustain", body: "An accepted IA that defaults in 90 days means the levy comes back — and harder. We negotiate to what the financials actually support." },
      { title: "Ignoring required estimated payments", body: "For self-employed taxpayers, missing the next estimate is automatic IA default — and the levy reissues." },
    ],
    alternatives: [
      { name: "CDP appeal (Form 12153)", whenItFits: "Within 30 days of LT11 — buys time, often more leverage than a straight resolution." },
      { name: "CNC hardship", whenItFits: "You can't sustain even minimum payments — pause everything." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "How much can the IRS take from my paycheck?", a: "The IRS uses Publication 1494 exemption tables, which are based on filing status and dependents — not a percentage. For a single filer with no dependents, the exempt amount is approximately $1,100/month; everything above goes to the IRS until the balance is paid or the levy is released." },
      { q: "Can the IRS garnish 1099 / contractor payments?", a: "Yes — the IRS can issue a one-time levy to any payer holding funds owed to you (clients, payment processors). These are not continuous like wage levies but can be devastating in single hits." },
      { q: "Will my employer fire me?", a: "Federal law (Consumer Credit Protection Act) prohibits termination based on a single garnishment. Multiple garnishments may not be protected — fast release matters." },
    ],
    citations: [
      { label: "IRC § 6331 — Levy and distraint", url: "https://www.law.cornell.edu/uscode/text/26/6331" },
      { label: "IRS Publication 1494 — Levy Exemption Tables", url: "https://www.irs.gov/pub/irs-pdf/p1494.pdf" },
      { label: "IRM 5.11 — Notice of Levy", url: "https://www.irs.gov/irm/part5/irm_05-011-001" },
    ],
  },

  "bank-levy": {
    quickAnswerBullets: [
      "21-day statutory hold under IRC § 6332(c) — bank freezes funds, then remits to IRS.",
      "Levy attaches only to funds in the account on the date of receipt — not future deposits.",
      "Most releases require active resolution + hardship documentation within the 21-day window.",
    ],
    urgency: {
      headline: "Day 22, the bank sends your money to the IRS. There is no extension.",
      points: [
        "The 21-day clock runs from when the bank receives Form 668-A — not when you discover the freeze.",
        "Subsequent deposits after the levy date are NOT frozen — you can still pay rent and bills if you act quickly.",
        "Once funds leave the bank, recovery requires a wrongful-levy claim that takes months and rarely succeeds.",
      ],
    },
    stats: [
      { value: "21 days", label: "Statutory holding period before bank remits", source: "IRC § 6332(c)" },
      { value: "Form 668-A", label: "Bank levy notice", source: "IRS.gov" },
      { value: "1 hit", label: "Bank levy is one-time — not continuous like wage levy", source: "IRS.gov" },
    ],
    howItWorks: [
      { title: "Confirm levy date and balance frozen", body: "We pull the IRS file and confirm the exact date the bank received Form 668-A and the dollar amount held. The 21-day countdown is unforgiving." },
      { title: "Hardship release vs. resolution release", body: "If the frozen funds are needed for rent, utilities, payroll — we file a hardship release request with documentation. Otherwise we put an IA or CNC in front of the IRS to trigger release." },
      { title: "Coordinate with the bank", body: "Even after IRS issues Form 668-D, the bank needs the release in hand before unfreezing. We confirm receipt on both ends." },
      { title: "Long-term resolution to prevent re-levy", body: "One released bank levy does not prevent another. Long-term IA, OIC, or CNC is the only durable fix." },
    ],
    disqualifiers: [
      "The freeze is from a state revenue department, court order, or private creditor — different process.",
      "The frozen account is not yours (joint with non-liable party) — wrongful-levy claim instead.",
    ],
    timeline: [
      { phase: "Engagement (Day 1–2)", duration: "Same day", what: "POA filed; bank contacted; IRS Collections engaged." },
      { phase: "Release negotiation (Day 2–14)", duration: "1–2 weeks", what: "Hardship documentation or resolution proposal; release request filed." },
      { phase: "Release confirmation (before Day 21)", duration: "Day 14–20", what: "Form 668-D issued and confirmed received by bank." },
    ],
    costFactors: [
      "How many days remain in the 21-day window.",
      "Whether unfiled returns block resolution.",
      "Hardship vs. structured-resolution path.",
    ],
    mistakes: [
      { title: "Moving funds out of other accounts before engaging", body: "Transfers immediately after a levy can look like dissipation. We coordinate with you before any movement." },
      { title: "Assuming the freeze covers future deposits", body: "It doesn't. You can use accounts normally for new deposits — but verify with us first." },
      { title: "Waiting to call until 'after the holiday weekend'", body: "The 21-day clock does not pause for weekends or holidays. Lost days are lost." },
    ],
    alternatives: [
      { name: "CDP appeal (Form 12153) — if within 30 days of LT11", whenItFits: "Wider leverage and stops further collection while pending." },
      { name: "Wrongful levy claim (Form 911 / TAS)", whenItFits: "Account belonged to a non-liable party or funds were exempt." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "Can the IRS levy my joint bank account?", a: "Yes — the IRS can levy a joint account even if only one holder owes. The non-liable spouse can file a wrongful-levy claim for their share, but it's far easier to release the levy before remittance." },
      { q: "What if my account had recent direct deposits of Social Security?", a: "Certain federal benefits are exempt from levy. We use those exemptions to release covered amounts." },
      { q: "Does the IRS levy business operating accounts?", a: "Yes, and the impact is often catastrophic. We treat business levies as same-day emergencies with payroll-protection documentation." },
    ],
    citations: [
      { label: "IRC § 6332 — Surrender of property subject to levy", url: "https://www.law.cornell.edu/uscode/text/26/6332" },
      { label: "IRM 5.11.4 — Bank Levies", url: "https://www.irs.gov/irm/part5/irm_05-011-004" },
      { label: "IRS Form 668-A — Notice of Levy", url: "https://www.irs.gov/forms-pubs" },
    ],
  },

  "tax-liens": {
    quickAnswerBullets: [
      "Federal tax lien arises automatically under IRC § 6321 once assessed, demanded, and unpaid.",
      "Notice of Federal Tax Lien (NFTL) on Form 668(Y) is the public filing that damages credit and title.",
      "Three remedies: withdrawal (NFTL erased), discharge (specific property freed), subordination (junior to refinance).",
    ],
    urgency: {
      headline: "An NFTL on record blocks home sales, refinances, and many business loans — quietly, indefinitely.",
      points: [
        "Selling a home with an NFTL requires discharge (Form 14135) — closings collapse without it.",
        "Refinancing requires subordination (Form 14134) — lenders won't fund junior to the IRS.",
        "NFTLs no longer appear on consumer credit reports but still surface on title searches, mortgage UW, and business credit pulls.",
      ],
    },
    stats: [
      { value: "Form 668(Y)", label: "Notice of Federal Tax Lien", source: "IRS.gov" },
      { value: "Form 12277", label: "Application for Withdrawal of Filed NFTL", source: "IRS.gov" },
      { value: "$25K", label: "Direct-debit IA withdrawal threshold", source: "Fresh Start Initiative" },
    ],
    howItWorks: [
      { title: "Withdrawal (NFTL removed from public record)", body: "Form 12277. Qualifying paths: direct-debit IA under $25K; lien filed in error; withdrawal facilitates collection; or full payment. Withdrawal is the strongest remedy — it's as if the NFTL never filed." },
      { title: "Discharge (specific property freed)", body: "Form 14135. Used when selling property — the IRS releases the lien from that property in exchange for its share of proceeds. Closings can proceed." },
      { title: "Subordination (junior to a new lender)", body: "Form 14134. Used when refinancing — the IRS keeps its lien but agrees to sit behind the new loan. Refis can proceed." },
      { title: "Release (after full payment or expired CSED)", body: "Automatic within 30 days of full payment under IRC § 6325(a). We follow up to ensure the release is recorded." },
    ],
    disqualifiers: [
      "You're not in compliance (unfiled returns, no IA) — withdrawal requires current compliance.",
      "Property has no equity above existing senior liens — discharge under § 6325(b)(2)(B) may still work.",
    ],
    timeline: [
      { phase: "Withdrawal application", duration: "30–60 days", what: "Form 12277 + supporting documentation submitted; IRS reviews and records withdrawal." },
      { phase: "Discharge or subordination", duration: "30–45 days", what: "Standard processing — must be timed to closing or refi deadline." },
      { phase: "Release (after payoff)", duration: "30 days", what: "Automatic under § 6325(a)." },
    ],
    costFactors: [
      "Which remedy applies (withdrawal is typically more involved than release).",
      "Whether an IA must first be set up to qualify for withdrawal.",
      "Coordination with title companies, lenders, and closing timelines.",
    ],
    mistakes: [
      { title: "Confusing lien release with lien withdrawal", body: "Release leaves the NFTL on public record marked released; withdrawal erases it entirely. Always pursue withdrawal when eligible." },
      { title: "Trying to refi without subordination", body: "Lenders will not fund. The Form 14134 process must start 45+ days before closing." },
      { title: "Letting the IRS file the NFTL when it could have been avoided", body: "Resolving the balance before the NFTL is filed (or appealing under CDP) is far easier than removing it after." },
    ],
    alternatives: [
      { name: "Pay-off via 401(k) or refinance proceeds", whenItFits: "Liquidity exists — full payment triggers automatic release and easy withdrawal." },
      { name: "OIC + lien withdrawal post-acceptance", whenItFits: "Long-term debt reduction is the goal and lien is a secondary problem." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "Will paying my balance remove the lien?", a: "Full payment triggers a release within 30 days. To remove the NFTL from public record, file Form 12277 for withdrawal — release alone leaves a marked entry." },
      { q: "Can a lien be filed without warning?", a: "The IRS must send Letter 3172 (Notice of Federal Tax Lien Filing) within 5 business days. You then have 30 days to request a Collection Due Process (CDP) hearing." },
      { q: "Does a federal tax lien attach to property I buy later?", a: "Yes — the lien attaches to all property and rights to property, current and after-acquired, until the balance is satisfied or the CSED expires." },
    ],
    citations: [
      { label: "IRC § 6321 — Lien for taxes", url: "https://www.law.cornell.edu/uscode/text/26/6321" },
      { label: "IRC § 6325 — Release of lien", url: "https://www.law.cornell.edu/uscode/text/26/6325" },
      { label: "IRS Form 12277 — Withdrawal", url: "https://www.irs.gov/forms-pubs/about-form-12277" },
    ],
  },

  "irs-seizures": {
    quickAnswerBullets: [
      "IRS seizure of physical assets requires managerial approval and is rare — but real (IRC § 6331(j)).",
      "Principal residence seizure requires federal court approval under IRC § 6334(e).",
      "Most seizures are stopped by CDP appeal, hardship release, or expedited resolution.",
    ],
    urgency: {
      headline: "Once seizure begins, recovery is dramatically harder than prevention.",
      points: [
        "Letter 1058 / LT11 starts a 30-day window — after which physical seizure becomes possible.",
        "Equipment, vehicles, and inventory can be tagged, removed, and sold at minimum bid (~80% of fair market value).",
        "Bond posting under IRC § 7426 to recover seized property is expensive and time-sensitive.",
      ],
    },
    stats: [
      { value: "§ 6334(e)", label: "Principal residence requires court approval", source: "IRC § 6334" },
      { value: "30 days", label: "Final Notice window before seizure authority", source: "IRC § 6330" },
      { value: "Form 12153", label: "Collection Due Process appeal", source: "IRS.gov" },
    ],
    howItWorks: [
      { title: "Emergency engagement & CDP appeal", body: "If you're within the 30-day window after Letter 1058 / LT11, we file Form 12153 (CDP appeal) — collections stop while pending." },
      { title: "Negotiate alternative resolution", body: "Most seizure cases settle into an IA, CNC, or pending OIC during the CDP appeal." },
      { title: "Hardship and economic harm arguments", body: "Where IA / CNC aren't immediately available, we present economic-harm arguments and request Taxpayer Advocate Service (Form 911) intervention." },
      { title: "Long-term resolution", body: "Closing the loop with an IA, OIC, or full payment — and pursuing lien withdrawal where eligible." },
    ],
    disqualifiers: [
      "You're past the CDP window and have ignored all prior notices — Equivalent Hearing only.",
      "Seizure is from a non-IRS creditor — different process entirely.",
    ],
    timeline: [
      { phase: "Emergency engagement", duration: "Same day", what: "POA + immediate IRS Collections contact." },
      { phase: "CDP appeal", duration: "3–9 months", what: "Pending appeal halts seizure; Appeals negotiates resolution." },
      { phase: "Long-term resolution", duration: "1–12 months", what: "IA / OIC / CNC put in place." },
    ],
    costFactors: [
      "Whether a CDP window is still open or only Equivalent Hearing is available.",
      "Volume of assets identified for seizure.",
      "Need for TAS intervention.",
    ],
    mistakes: [
      { title: "Ignoring LT11 / Letter 1058", body: "The 30-day CDP window is the strongest single protection in the code. Missing it forfeits Tax Court review of the collection action." },
      { title: "Self-help asset transfers", body: "Transferring assets after notice can be a fraudulent transfer — exposing you and the recipient." },
    ],
    alternatives: [
      { name: "Bankruptcy (Chapter 13)", whenItFits: "Combined with a confirmed plan, bankruptcy stays IRS seizure of non-fraud-related liabilities." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "Can the IRS take my house?", a: "Legally yes, but it requires federal court approval under IRC § 6334(e) and is extraordinarily rare. We treat residence-seizure threats as the highest-priority engagement." },
      { q: "How is seized property sold?", a: "By public auction at a statutory minimum bid roughly 80% of forced-sale value. Sale proceeds apply to the tax debt; excess returns to the taxpayer." },
    ],
    citations: [
      { label: "IRC § 6334 — Property exempt from levy", url: "https://www.law.cornell.edu/uscode/text/26/6334" },
      { label: "IRC § 6331 — Levy and distraint", url: "https://www.law.cornell.edu/uscode/text/26/6331" },
      { label: "IRM 5.10 — Seizure and Sale", url: "https://www.irs.gov/irm/part5/irm_05-010-001" },
    ],
  },

  "stop-collections": {
    quickAnswerBullets: [
      "Three primary collection holds: Power of Attorney (Form 2848), CDP appeal (Form 12153), pending resolution.",
      "POA filed → IRS legally required to contact your representative, not you.",
      "CDP appeal within 30 days of LT11 stops all collection while pending.",
    ],
    urgency: {
      headline: "Every IRS letter ignored shortens the window for the strongest collection holds.",
      points: [
        "The 30-day CDP appeal window starts from the LT11 / Letter 1058 mailing date — not receipt.",
        "Once that window passes, only Equivalent Hearing is available — no Tax Court review.",
        "Each ignored notice raises your case from ACS automation to a Revenue Officer — much harder to negotiate.",
      ],
    },
    stats: [
      { value: "Form 2848", label: "Power of Attorney — instant communication shield", source: "IRS.gov" },
      { value: "30 days", label: "CDP appeal deadline from LT11/Letter 1058", source: "IRC § 6330(a)" },
      { value: "Form 12153", label: "Request for CDP Hearing", source: "IRS.gov" },
    ],
    howItWorks: [
      { title: "Power of Attorney (same day)", body: "Form 2848 redirects all IRS contact to our office. Calls to your home and workplace stop." },
      { title: "Collection Due Process (CDP) appeal", body: "Form 12153 within 30 days of LT11/Letter 1058 stops collection and gives Tax Court review rights." },
      { title: "Pending resolution holds", body: "A pending OIC, IA, or CNC request triggers a collection hold while under review." },
      { title: "Taxpayer Advocate Service", body: "Form 911 for systemic delays or imminent economic harm — TAS can issue Taxpayer Assistance Orders." },
    ],
    disqualifiers: [
      "You owe nothing — pull transcripts before engaging.",
      "You're outside CDP window and resolution-eligible — go straight to IA / OIC / CNC instead.",
    ],
    timeline: [
      { phase: "Communication shield", duration: "Same day", what: "POA filed; IRS contact redirected." },
      { phase: "Hold trigger", duration: "1–4 weeks", what: "CDP appeal, hardship, or pending resolution submitted." },
      { phase: "Long-term resolution", duration: "1–12 months", what: "Final IA / OIC / CNC put in place." },
    ],
    costFactors: [
      "Whether emergency hold is needed first vs. straight to resolution.",
      "Number of years and complexity of underlying debt.",
    ],
    mistakes: [
      { title: "Answering the door for a Revenue Officer", body: "ROs make field visits to pressure resolution. After POA, the RO must come through us. We close that door immediately." },
      { title: "Filing CDP late", body: "After 30 days, only Equivalent Hearing — no Tax Court review, no automatic collection hold." },
    ],
    alternatives: [
      { name: "Bankruptcy automatic stay", whenItFits: "Income-tax debt and other liabilities together — coordinated with bankruptcy counsel." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "Once you take over, will the IRS stop calling me?", a: "Yes — after Form 2848 is filed, the IRS is required to contact us, not you. Calls and field visits to you should stop within days." },
      { q: "Can I file a CDP appeal just to buy time?", a: "CDP appeals must raise genuine collection-alternative or liability issues — frivolous appeals are subject to penalty. We only file when we have substantive grounds, which is the vast majority of active-collection cases." },
    ],
    citations: [
      { label: "IRC § 6330 — Notice and opportunity for hearing before levy", url: "https://www.law.cornell.edu/uscode/text/26/6330" },
      { label: "IRS Form 2848 — Power of Attorney", url: "https://www.irs.gov/forms-pubs/about-form-2848" },
      { label: "Taxpayer Advocate Service", url: "https://www.taxpayeradvocate.irs.gov/" },
    ],
  },

  // ───────────── AUDIT DEFENSE ─────────────
  "irs-audit-representation": {
    quickAnswerBullets: [
      "Three audit types: correspondence (by mail), office (IRS office), field (your business/home).",
      "With Form 2848 Power of Attorney, you are not required to meet with the auditor at all.",
      "Audit defense is governed by IRM Part 4 and the Taxpayer Bill of Rights.",
    ],
    urgency: {
      headline: "What you say in the first conversation defines the scope of the entire audit.",
      points: [
        "Audits expand based on volunteered information and unrequested documents — most expansions are taxpayer-caused.",
        "Statute of limitations on assessment is generally 3 years (6 years if >25% income omitted) — extending it is rarely in your interest.",
        "Once an examination report (Form 4549) issues, the 30-day letter window starts — miss it and the only forum left is Tax Court.",
      ],
    },
    stats: [
      { value: "0.38%", label: "Overall individual return audit rate (recent IRS data)", source: "IRS Data Book Table 17" },
      { value: "3 yr", label: "Standard statute of limitations on assessment", source: "IRC § 6501(a)" },
      { value: "Form 2848", label: "POA — auditor must deal with your representative", source: "IRS.gov" },
    ],
    howItWorks: [
      { title: "Power of Attorney + Information Document Request (IDR) triage", body: "We file Form 2848 immediately. The auditor's IDR is reviewed line-by-line; we narrow scope, push back on overbroad requests, and control timing." },
      { title: "Document production with attorney-client-style privilege analysis", body: "Section 7525 federally authorized tax practitioner privilege applies in many audits. We assess privilege before any document leaves your hands." },
      { title: "Examination negotiation", body: "Most issues are resolved at the examiner level — adjustments, partial concessions, schedule defenses. The goal is closing at exam without a 30-day letter." },
      { title: "30-day letter → Appeals → Tax Court", body: "If exam doesn't close favorably, we file a written protest to the Independent Office of Appeals. Appeals can settle based on hazards of litigation — a different standard than examiners. If Appeals fails, we preserve Tax Court rights." },
    ],
    disqualifiers: [
      "Criminal referral suspected (CI involvement, eggshell audit) — engage a tax attorney for privilege protection.",
      "You're under audit for fraud — different procedural posture entirely.",
    ],
    timeline: [
      { phase: "Engagement & scope analysis", duration: "1–2 weeks", what: "POA filed; IDR reviewed; production strategy built." },
      { phase: "Document production & meetings", duration: "1–6 months", what: "Auditor handled directly by us; meetings attended in your stead." },
      { phase: "Closing or appeal", duration: "1–9 months", what: "Closing letter, or 30-day letter into Appeals." },
    ],
    costFactors: [
      "Audit type (correspondence cheaper than field).",
      "Issues raised (Schedule C and pass-through entities are more involved).",
      "Whether Appeals or Tax Court is required.",
    ],
    mistakes: [
      { title: "Volunteering documents not requested", body: "Every document opens potential new issues. We produce only what's specifically requested, in the narrowest defensible form." },
      { title: "Extending the statute of limitations without thought", body: "Form 872 extensions almost always favor the IRS. We negotiate against extension or limit it sharply." },
      { title: "Meeting the auditor without representation", body: "Auditor questions are not friendly fact-finding — they're building a record. Skip the meeting and let us handle it." },
    ],
    alternatives: [
      { name: "Audit Reconsideration", whenItFits: "Audit already closed by default — reopen with new evidence." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "Do I have to meet the IRS auditor?", a: "No. With Form 2848 Power of Attorney, your representative meets the auditor in your stead. Most clients never speak to the auditor at all." },
      { q: "What's the difference between a correspondence audit and a field audit?", a: "Correspondence audits are handled by mail and typically address a narrow issue (charitable, EITC). Field audits happen at your business/home and are more comprehensive. Office audits sit in between." },
      { q: "Can the IRS extend my audit beyond 3 years?", a: "Only if you sign Form 872 to extend, the IRS proves >25% income omission (6-year rule), or fraud (unlimited statute). We almost never recommend voluntary extension." },
    ],
    citations: [
      { label: "IRC § 6501 — Limitations on assessment", url: "https://www.law.cornell.edu/uscode/text/26/6501" },
      { label: "IRC § 7525 — Federally authorized tax practitioner privilege", url: "https://www.law.cornell.edu/uscode/text/26/7525" },
      { label: "IRS Publication 1 — Your Rights as a Taxpayer", url: "https://www.irs.gov/pub/irs-pdf/p1.pdf" },
    ],
  },

  "irs-appeals": {
    quickAnswerBullets: [
      "IRS Independent Office of Appeals is statutorily independent (Taxpayer First Act § 1001).",
      "Settlement standard is hazards of litigation — not just facts and law.",
      "Two main types: post-exam protest (>$25K requires formal written protest) and Collection Due Process (CDP).",
    ],
    urgency: {
      headline: "Appeals deadlines are short — and missing them forfeits the best resolution forum.",
      points: [
        "Post-exam appeals: 30 days from the 30-day letter.",
        "CDP appeals: 30 days from LT11/Letter 1058 (Equivalent Hearing if late — no Tax Court).",
        "Appeals officers have authority to settle that examiners and revenue officers do not.",
      ],
    },
    stats: [
      { value: "Hazards", label: "Settlement standard at Appeals (litigation risk weighted)", source: "IRM 8.6.4" },
      { value: "30 days", label: "Standard window from 30-day letter", source: "IRS.gov" },
      { value: "Form 12153", label: "Request for CDP / Equivalent Hearing", source: "IRS.gov" },
    ],
    howItWorks: [
      { title: "Written protest", body: "For balances >$25K, a detailed written protest is required: facts, law, position, and (critically) hazards-of-litigation argument quantifying IRS exposure." },
      { title: "Appeals conference", body: "Telephone or in-person conference with the Appeals Officer. Settlement discussions, not factual cross-examination." },
      { title: "Settlement or unagreed close", body: "Most cases settle. Unagreed cases proceed to Tax Court (deficiency) or U.S. District Court / Court of Federal Claims (refund litigation)." },
    ],
    disqualifiers: [
      "30-day window has expired — pivot to Audit Reconsideration or Tax Court (90-day letter window).",
      "Frivolous positions — Appeals will not hear and § 6702 penalty applies.",
    ],
    timeline: [
      { phase: "Protest drafting", duration: "2–4 weeks", what: "Hazards analysis and written protest filed." },
      { phase: "Appeals assignment & conference", duration: "3–9 months", what: "Officer assignment, document exchange, conference." },
      { phase: "Settlement or trial preparation", duration: "1–6 months", what: "Most cases close; unagreed proceed to court." },
    ],
    costFactors: [
      "Complexity and dollar amount at stake.",
      "Whether Tax Court litigation prep is needed.",
    ],
    mistakes: [
      { title: "Repeating the exam argument verbatim", body: "Appeals officers want hazards analysis, not a re-run of exam. We reframe for the new forum." },
      { title: "Missing the small-case (SCAP) opportunity", body: "Cases ≤$50K may qualify for Small Case Appeals — faster, more flexible." },
    ],
    alternatives: [
      { name: "Tax Court petition (90-day letter)", whenItFits: "Skip Appeals to file directly in Tax Court — useful for strategic reasons." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "Is Appeals really independent from the auditor?", a: "Yes — the Taxpayer First Act of 2019 codified the independence of the Independent Office of Appeals (IRC § 7803(e)). Appeals officers are barred from ex parte communications with examination personnel." },
      { q: "Can I represent myself at Appeals?", a: "Yes, but the hazards-of-litigation standard rewards experienced advocates who understand how Tax Court would view the case." },
    ],
    citations: [
      { label: "IRC § 7803(e) — Independent Office of Appeals", url: "https://www.law.cornell.edu/uscode/text/26/7803" },
      { label: "IRM Part 8 — Appeals", url: "https://www.irs.gov/irm/part8" },
      { label: "IRS Publication 5 — Your Appeal Rights", url: "https://www.irs.gov/pub/irs-pdf/p5.pdf" },
    ],
  },

  "cp2000-response": {
    quickAnswerBullets: [
      "CP2000 is an underreporter notice — not an audit — proposing adjustments based on third-party reporting mismatches.",
      "30-day response window; ignored CP2000s become Statutory Notices of Deficiency (90-day letter).",
      "Most CP2000s are wrong or incomplete — offsetting basis, expenses, and credits frequently overlooked.",
    ],
    urgency: {
      headline: "Day 31 of inaction, the IRS escalates to a 90-day letter — then assesses the full proposed tax.",
      points: [
        "IRS computers compare your 1040 to W-2, 1099, K-1, and broker reporting — discrepancies trigger automatic CP2000.",
        "1099-B stock sales without reported basis are the #1 source of inflated CP2000s — the IRS assumes $0 basis.",
        "If you partially agree, you can sign the partial agreement and dispute the rest — but only with a written response.",
      ],
    },
    stats: [
      { value: "AUR", label: "Automated Underreporter system that issues CP2000", source: "IRS.gov" },
      { value: "30 days", label: "Standard response window", source: "CP2000 notice" },
      { value: "90 days", label: "Statutory Notice of Deficiency window after CP2000 ignored", source: "IRC § 6213(a)" },
    ],
    howItWorks: [
      { title: "Reconcile every line", body: "We pull Wage & Income transcripts and match each item to your return. Many CP2000s double-count income already reported under a different code." },
      { title: "Identify offsets", body: "Basis on stock sales, deductible expenses on 1099-NEC income, cost basis on crypto disposals, qualified plan rollovers reported on 1099-R — these reduce or eliminate the proposed tax." },
      { title: "Written response with documentation", body: "We respond on the CP2000 form with a detailed explanation and supporting docs. Most disputes resolve at this step." },
      { title: "Escalation if needed", body: "If AUR rejects, we appeal to the IRS Independent Office of Appeals. If a Notice of Deficiency issues, we preserve Tax Court rights." },
    ],
    disqualifiers: [
      "The CP2000 is correct and the math is right — pay or set up a payment plan.",
      "The notice is fraud-related — coordinate with a tax attorney first.",
    ],
    timeline: [
      { phase: "Analysis & document gathering", duration: "1–2 weeks", what: "Transcripts pulled; reconciliation complete." },
      { phase: "Written response", duration: "1 week", what: "Response prepared and sent before 30-day deadline." },
      { phase: "IRS resolution", duration: "2–4 months", what: "Most cases close; some require Appeals follow-up." },
    ],
    costFactors: [
      "Volume of items in dispute.",
      "Whether basis or expense documentation must be reconstructed.",
      "Whether Appeals or Tax Court is required.",
    ],
    mistakes: [
      { title: "Ignoring the notice because 'the IRS already has my information'", body: "Inaction equals agreement. The IRS will assess the full proposed tax on day 91." },
      { title: "Just paying the proposed amount", body: "Most CP2000s overstate liability. Pay only after reconciliation." },
      { title: "Not reporting basis on stock sales", body: "The IRS assumes $0 basis for unreported transactions. Many CP2000s evaporate once basis is documented." },
    ],
    alternatives: [
      { name: "Amended return (Form 1040-X)", whenItFits: "Better when changes also affect other years — coordinate with CP2000 response." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "Is a CP2000 an audit?", a: "No — it's an automated proposal based on third-party data mismatches. But ignoring it produces the same end result as a defaulted audit." },
      { q: "What if I agree with some adjustments but not all?", a: "Sign and return the partial agreement portion, and dispute the rest in writing. Don't sign the full agreement if anything is wrong." },
      { q: "Can a CP2000 trigger a real audit?", a: "Yes — particularly for self-employed taxpayers where the response opens questions about Schedule C accuracy." },
    ],
    citations: [
      { label: "IRC § 6213 — Restrictions on assessment", url: "https://www.law.cornell.edu/uscode/text/26/6213" },
      { label: "IRM 4.19.3 — IMF Automated Underreporter", url: "https://www.irs.gov/irm/part4/irm_04-019-003" },
      { label: "IRS — Understanding Your CP2000 Notice", url: "https://www.irs.gov/individuals/understanding-your-cp2000-notice" },
    ],
  },

  "exam-reconsideration": {
    quickAnswerBullets: [
      "Available after a deficiency is assessed (default audit, missed response, or signed Form 4549) — IRM 4.13.",
      "No statutory deadline, but discretionary — must show new information or IRS error.",
      "Effective when the original audit closed by default or before key documents surfaced.",
    ],
    urgency: {
      headline: "Reconsideration is discretionary — the longer the gap, the harder the IRS pushes back.",
      points: [
        "Active collection (levy, garnishment) doesn't pause for reconsideration unless separately negotiated.",
        "New information must be material and previously unavailable.",
        "Refund years past the 3-year/2-year window are gone even if reconsideration succeeds.",
      ],
    },
    stats: [
      { value: "IRM 4.13", label: "Audit Reconsideration Procedures", source: "IRS.gov" },
      { value: "Form 12661", label: "Disputed Issue Verification", source: "IRS.gov" },
      { value: "No deadline", label: "Discretionary remedy — but timing matters", source: "IRM 4.13" },
    ],
    howItWorks: [
      { title: "Verify reconsideration is the right tool", body: "If a 90-day letter is still open, file Tax Court instead. If reconsideration was already denied, options narrow. We assess before filing." },
      { title: "Build the reconsideration package", body: "Letter requesting reconsideration + new documentation + Form 12661 if needed + reconciliation showing the corrected liability." },
      { title: "Negotiate with the reconsideration examiner", body: "Often a different examiner than the original — fresh review with new evidence." },
      { title: "Coordinate collection holds", body: "Reconsideration doesn't automatically pause collection — we negotiate a hold separately." },
    ],
    disqualifiers: [
      "Liability was paid in full — reconsideration not available, file refund claim instead.",
      "Issue was litigated in Tax Court or Appeals — res judicata bars reconsideration.",
      "Liability resulted from a closing agreement or signed Form 906.",
    ],
    timeline: [
      { phase: "Eligibility & document review", duration: "2–4 weeks", what: "Confirm reconsideration is correct path; gather new evidence." },
      { phase: "Submission", duration: "1–2 weeks", what: "Package filed with the appropriate IRS office." },
      { phase: "IRS review", duration: "3–9 months", what: "Examiner review and decision; possible Appeals." },
    ],
    costFactors: [
      "Volume of new evidence to compile.",
      "Whether collection holds must be negotiated in parallel.",
      "Whether Appeals is required after reconsideration denial.",
    ],
    mistakes: [
      { title: "Filing reconsideration when Tax Court is still open", body: "Tax Court provides independent review — reconsideration is discretionary. We file Tax Court when the 90-day window remains." },
      { title: "Submitting weak or unsubstantiated new evidence", body: "Reconsideration is denied without material new information. We curate aggressively." },
    ],
    alternatives: [
      { name: "Doubt-as-to-Liability OIC", whenItFits: "Liability is wrong but reconsideration isn't available — challenge via Form 656-L." },
      { name: "Refund claim (Form 843 / 1040-X)", whenItFits: "Liability already paid — recover via refund claim." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "Will reconsideration stop collections?", a: "Not automatically. We negotiate a separate hold while reconsideration is pending — often via TAS or hardship request." },
      { q: "Can I request reconsideration on a Substitute for Return (SFR) assessment?", a: "Yes — and this is one of the most successful reconsideration scenarios. Filing the original return with proper deductions almost always reduces the SFR balance significantly." },
    ],
    citations: [
      { label: "IRM 4.13 — Audit Reconsideration", url: "https://www.irs.gov/irm/part4/irm_04-013-001" },
      { label: "IRS Publication 3598 — Audit Reconsideration", url: "https://www.irs.gov/pub/irs-pdf/p3598.pdf" },
    ],
  },

  // ───────────── BUSINESS TAX ─────────────
  "payroll-tax": {
    quickAnswerBullets: [
      "Form 941 (quarterly) and Form 940 (annual FUTA) liabilities are the IRS's #1 collection priority.",
      "Trust Fund Recovery Penalty (IRC § 6672) pierces the corporate veil to assess owners and officers personally.",
      "Revenue Officers (not ACS) are typically assigned — enforcement moves fast.",
    ],
    urgency: {
      headline: "Payroll tax problems escalate faster than any other IRS matter.",
      points: [
        "Each missed deposit triggers Failure-to-Deposit penalties up to 15%, layered on the principal.",
        "Revenue Officer enforcement can include in-business CNC denial, lien filing, equipment levy, and federal injunction.",
        "Trust Fund Recovery Penalty assessment to owners can total 100% of the unpaid trust-fund portion (income + employee FICA).",
      ],
    },
    stats: [
      { value: "100%", label: "TFRP can assess 100% of trust-fund portion to responsible persons", source: "IRC § 6672" },
      { value: "15%", label: "Maximum failure-to-deposit penalty", source: "IRC § 6656" },
      { value: "RO", label: "Revenue Officer typically assigned to payroll cases", source: "IRM 5.7" },
    ],
    howItWorks: [
      { title: "Stop the bleeding — current compliance", body: "Resolution starts with becoming current on the most recent quarter's deposits and filings. Without that, the IRS will not negotiate." },
      { title: "In-business resolution strategy", body: "Options include in-business installment agreement, in-business CNC (rare), and structured payment plans negotiated with the Revenue Officer." },
      { title: "Trust Fund Recovery Penalty defense", body: "We represent the Form 4180 interview, contest responsible-person and willfulness elements, and protest TFRP assessment to Appeals." },
      { title: "Operational restructuring", body: "We help restructure payroll processing (third-party payroll service, separate deposit account) to prevent recurrence." },
    ],
    disqualifiers: [
      "Business is closed and TFRP already assessed personally — pivot to personal collections strategy.",
      "Owner is unwilling to bring current payroll into compliance — IRS will not negotiate while bleeding continues.",
    ],
    timeline: [
      { phase: "Stabilization", duration: "30 days", what: "Current compliance restored; POA + RO contact established." },
      { phase: "Resolution structuring", duration: "1–3 months", what: "IA negotiated; TFRP defense initiated if applicable." },
      { phase: "Long-term execution", duration: "12–60 months", what: "Payment plan execution + operational changes prevent recurrence." },
    ],
    costFactors: [
      "Number of quarters in arrears.",
      "Whether TFRP defense is required for one or multiple responsible persons.",
      "Whether the business needs operational restructuring of payroll process.",
    ],
    mistakes: [
      { title: "Continuing to fall behind while negotiating", body: "The IRS will not agree to any in-business resolution while new payroll deposits are missed. We make current-compliance non-negotiable." },
      { title: "Treating the Form 4180 interview casually", body: "The 4180 interview defines responsible-person and willfulness elements. Casual answers create TFRP exposure that's hard to unwind." },
      { title: "Personally guaranteeing or paying business payroll tax from personal funds before TFRP assessment", body: "Allocation matters. Without proper designation, payments are absorbed in ways that don't reduce TFRP exposure." },
    ],
    alternatives: [
      { name: "Chapter 11 reorganization", whenItFits: "Business viable, debt structure broken — coordinated with bankruptcy counsel." },
      { name: "Asset sale + new entity (carefully)", whenItFits: "TFRP transferee liability and successor-liability rules apply — only with skilled coordination." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "Can the IRS shut down my business?", a: "For payroll-tax non-compliance, yes — the IRS can pursue federal injunctions and seize equipment. Engagement before that point dramatically improves outcomes." },
      { q: "Will I be personally liable?", a: "If you are a 'responsible person' who 'willfully' failed to pay over withheld taxes, the Trust Fund Recovery Penalty (IRC § 6672) assesses 100% of the trust-fund portion personally. We defend both elements." },
      { q: "Can payroll tax be discharged in bankruptcy?", a: "Generally no — trust-fund taxes are non-dischargeable. Non-trust-fund portions (employer FICA, FUTA) may be dischargeable under specific conditions." },
    ],
    citations: [
      { label: "IRC § 6672 — Trust Fund Recovery Penalty", url: "https://www.law.cornell.edu/uscode/text/26/6672" },
      { label: "IRC § 6656 — Failure to deposit", url: "https://www.law.cornell.edu/uscode/text/26/6656" },
      { label: "IRM 5.7 — Trust Fund Compliance", url: "https://www.irs.gov/irm/part5/irm_05-007-001" },
    ],
  },

  "trust-fund-recovery": {
    quickAnswerBullets: [
      "TFRP under IRC § 6672 assesses 100% of unpaid trust-fund payroll tax personally against responsible persons.",
      "Two elements: 'responsible person' + 'willfulness' — both must be established.",
      "Letter 1153 + Form 2751 is the proposed assessment; you have 60 days to protest.",
    ],
    urgency: {
      headline: "Once TFRP is assessed, it follows you personally — even if the business closes.",
      points: [
        "60-day window from Letter 1153 to file a protest with IRS Appeals.",
        "After assessment, collection proceeds like any other personal tax debt — levy, garnishment, lien.",
        "Multiple responsible persons can each be assessed 100% — the IRS collects once but pursues all.",
      ],
    },
    stats: [
      { value: "100%", label: "Of unpaid trust-fund portion (income + employee FICA)", source: "IRC § 6672(a)" },
      { value: "60 days", label: "From Letter 1153 to file protest", source: "IRM 5.7.5" },
      { value: "Form 4180", label: "Responsible-person interview", source: "IRS.gov" },
    ],
    howItWorks: [
      { title: "Form 4180 interview defense", body: "The IRS interview tests responsibility and willfulness across 30+ questions. We prepare you, attend the interview, and frame answers to avoid creating unnecessary exposure." },
      { title: "Identify the actual responsible parties", body: "Many TFRP cases incorrectly target multiple parties. We document who actually had check-writing and decision-making authority during the bleeding period." },
      { title: "Protest Letter 1153 to Appeals", body: "Written protest with factual rebuttal of responsibility, willfulness, or both. Appeals settlement uses hazards-of-litigation standard." },
      { title: "Post-assessment defense", body: "If assessed, we pursue OIC (Doubt as to Liability), allocate prior payments, or pursue § 6672 contribution actions among multiple responsible persons." },
    ],
    disqualifiers: [
      "You signed payroll checks knowing payroll tax wasn't being deposited and had no plan to cure — willfulness is established.",
      "Clear documentary evidence of responsibility and willfulness exists — pivot to settlement strategy.",
    ],
    timeline: [
      { phase: "Form 4180 preparation & interview", duration: "2–6 weeks", what: "Pre-interview prep; attended representation." },
      { phase: "Letter 1153 protest", duration: "30–60 days", what: "Written Appeals protest filed within window." },
      { phase: "Appeals + resolution", duration: "6–12 months", what: "Appeals conference; settlement or assessment." },
    ],
    costFactors: [
      "Number of quarters under assessment.",
      "Number of potentially responsible parties.",
      "Whether Appeals or Doubt-as-to-Liability OIC is required.",
    ],
    mistakes: [
      { title: "Attending the Form 4180 interview without representation", body: "Casual answers about check-signing or deposit decisions create willfulness findings that are very hard to reverse." },
      { title: "Paying personally without allocation", body: "Personal payments toward business payroll tax must be designated 'trust fund first' in writing to reduce TFRP exposure." },
      { title: "Assuming the business closing solves the problem", body: "TFRP is personal. Closing the business actually accelerates IRS personal collection." },
    ],
    alternatives: [
      { name: "Doubt-as-to-Liability OIC (Form 656-L)", whenItFits: "TFRP was incorrectly assessed — challenge the underlying liability." },
      { name: "Section 6672(d) contribution action", whenItFits: "Multiple responsible persons — recover paid TFRP from co-responsible parties." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "What does 'willful' mean for TFRP?", a: "Willful means a conscious, voluntary decision to use trust-fund money for something other than paying the IRS — even if to keep the business alive. It does not require bad intent." },
      { q: "Can a bookkeeper be assessed TFRP?", a: "Yes, if they had check-writing or deposit authority and knew about the unpaid liability. Authority + knowledge are the trigger, not job title." },
      { q: "Can TFRP be discharged in bankruptcy?", a: "No — TFRP is non-dischargeable under 11 U.S.C. § 523(a)(1)(A) as a tax 'required to be collected'." },
    ],
    citations: [
      { label: "IRC § 6672 — Failure to collect and pay over tax", url: "https://www.law.cornell.edu/uscode/text/26/6672" },
      { label: "IRM 5.7.5 — Trust Fund Compliance — TFRP", url: "https://www.irs.gov/irm/part5/irm_05-007-005" },
      { label: "IRS Form 4180", url: "https://www.irs.gov/forms-pubs" },
    ],
  },

  "employment-tax": {
    quickAnswerBullets: [
      "Covers Form 941 (quarterly), Form 940 (FUTA), and worker-classification (W-2 vs. 1099) disputes.",
      "SS-8 determination requests can be filed by workers or initiated by the IRS — outcomes are binding.",
      "Section 530 relief (Revenue Act of 1978) can provide safe harbor against reclassification assessments.",
    ],
    urgency: {
      headline: "Worker reclassification turns 1099 contractors into W-2 employees retroactively — at full payroll-tax cost.",
      points: [
        "Reclassification assessments can exceed annual revenue for small contractor-heavy businesses.",
        "State unemployment and workers' comp agencies share data — one classification finding cascades.",
        "Section 530 safe harbor requires reasonable basis + consistent treatment + 1099 filing — all three.",
      ],
    },
    stats: [
      { value: "§ 530", label: "Safe harbor under Revenue Act of 1978", source: "P.L. 95-600" },
      { value: "Form SS-8", label: "Worker status determination", source: "IRS.gov" },
      { value: "Form 941-X", label: "Amended quarterly employment tax return", source: "IRS.gov" },
    ],
    howItWorks: [
      { title: "Compliance triage", body: "Identify every unfiled or amended 941/940, every classification exposure, and every state cascade risk." },
      { title: "Section 530 analysis", body: "We document reasonable basis (industry practice, prior audit, judicial precedent), consistent treatment, and 1099 filing history to establish safe-harbor protection." },
      { title: "Classification audit defense", body: "We represent in IRS Employment Tax Examination and SS-8 determinations, controlling document production and witness statements." },
      { title: "Resolution of liabilities", body: "Once exposure is quantified, we negotiate Classification Settlement Program (CSP) terms, Section 3509 reduced rates, or installment agreements." },
    ],
    disqualifiers: [
      "Workers were treated inconsistently (some W-2, some 1099 for the same role) — Section 530 unavailable.",
      "No 1099s were filed for contractors — Section 530 safe harbor is forfeited.",
    ],
    timeline: [
      { phase: "Compliance scope", duration: "2–4 weeks", what: "Identify all unfiled periods, classification exposure, state cascades." },
      { phase: "Audit / SS-8 defense", duration: "3–12 months", what: "Representation through examination and determination process." },
      { phase: "Resolution", duration: "1–12 months", what: "CSP, IA, OIC, or full payment of resulting liability." },
    ],
    costFactors: [
      "Number of workers and years involved in reclassification.",
      "Whether IRS Employment Tax Examination is open vs. SS-8 only.",
      "State cascade defense (unemployment, workers' comp).",
    ],
    mistakes: [
      { title: "Reclassifying mid-audit without strategy", body: "Voluntary reclassification during an open exam often increases exposure. We coordinate any reclassification with CSP or VCSP strategy." },
      { title: "Skipping 1099 filings on contractors", body: "Missing 1099s eliminate Section 530 protection. We catch this in pre-audit compliance review." },
      { title: "Talking to the IRS about prior classification history without preparation", body: "Casual answers create the inconsistent-treatment finding that destroys Section 530." },
    ],
    alternatives: [
      { name: "Voluntary Classification Settlement Program (VCSP)", whenItFits: "Proactive reclassification with 10% of one year's employment tax — favorable terms but specific eligibility." },
      { name: "Classification Settlement Program (CSP)", whenItFits: "Mid-audit settlement — favorable when full reclassification would otherwise apply." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "What's the difference between SS-8 and an employment tax audit?", a: "SS-8 is a single-worker determination requested by worker or business. Employment Tax Examination is broader — multiple workers, multiple years, with assessment authority." },
      { q: "Can I switch contractors to W-2 going forward?", a: "Yes, but how you do it matters. Prospective reclassification under VCSP gives favorable settlement on the past; uncoordinated reclassification can trigger backward-looking exam." },
    ],
    citations: [
      { label: "Section 530, Revenue Act of 1978", url: "https://www.irs.gov/businesses/small-businesses-self-employed/section-530-employment-tax-relief-requirements" },
      { label: "IRS Form SS-8", url: "https://www.irs.gov/forms-pubs/about-form-ss-8" },
      { label: "IRM 4.23 — Employment Tax", url: "https://www.irs.gov/irm/part4/irm_04-023-001" },
    ],
  },

  "sales-and-use-tax": {
    quickAnswerBullets: [
      "Post-Wayfair (South Dakota v. Wayfair, 2018), economic nexus exists in 45+ states.",
      "Voluntary Disclosure Agreements (VDA) limit lookback to 3–4 years vs. unlimited for non-filers.",
      "Each state has its own appeals, statute, and amnesty rules — coordination is non-trivial.",
    ],
    urgency: {
      headline: "Unfiled sales-tax exposure compounds across every state where you've crossed economic-nexus thresholds.",
      points: [
        "Most states have no statute of limitations on unfiled sales tax — exposure grows indefinitely.",
        "Marketplace facilitator laws shifted collection to platforms — but historical pre-shift exposure remains.",
        "Audit findings in one state are shared via the Multistate Tax Commission, accelerating cascading exposure.",
      ],
    },
    stats: [
      { value: "45+", label: "States with economic nexus rules post-Wayfair", source: "Sales Tax Institute" },
      { value: "VDA", label: "Voluntary Disclosure Agreement — limits lookback", source: "State DOR programs" },
      { value: "MTC", label: "Multistate Tax Commission VDA program", source: "MTC.gov" },
    ],
    howItWorks: [
      { title: "Nexus study", body: "We map sales by state, identify economic-nexus thresholds crossed, and quantify exposure year by year." },
      { title: "VDA strategy", body: "Where appropriate, we file VDA applications (state-by-state or through MTC) to limit lookback, waive penalties, and quantify liability cleanly." },
      { title: "Audit defense", body: "For states with open audits, we represent through the audit, contest assessments, and negotiate settlements." },
      { title: "Forward compliance", body: "We coordinate with your sales-tax automation (Avalara, TaxJar) to ensure forward filings are clean." },
    ],
    disqualifiers: [
      "You've already received an audit notice from the state — VDA is no longer available; pivot to audit defense.",
      "Your exposure is single-state with minimal volume — simpler registration + back-filing may be more cost-effective than VDA.",
    ],
    timeline: [
      { phase: "Nexus study & strategy", duration: "3–6 weeks", what: "Per-state exposure quantified; strategy selected." },
      { phase: "VDA negotiation / audit defense", duration: "3–12 months", what: "Per-state engagement." },
      { phase: "Forward compliance", duration: "Ongoing", what: "Registration, filing setup, automation." },
    ],
    costFactors: [
      "Number of states with exposure.",
      "Whether VDA is available vs. audit defense.",
      "Complexity of product taxability (SaaS, digital goods, services).",
    ],
    mistakes: [
      { title: "Registering before VDA", body: "Voluntary registration ends VDA eligibility. Always run the VDA analysis first." },
      { title: "Ignoring use tax", body: "Use tax exposure (purchased goods/services used in-state) often equals or exceeds sales-tax exposure for service businesses." },
    ],
    alternatives: [
      { name: "MTC Multistate Voluntary Disclosure Program", whenItFits: "Exposure spans many states — single application covers participating jurisdictions." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "Do I owe sales tax for years before I crossed economic-nexus thresholds?", a: "Generally no — economic nexus is forward-looking from the date you crossed the threshold (volume or transaction count). We document the threshold-crossing date for each state." },
      { q: "Are SaaS and digital services taxable?", a: "It varies sharply by state. About half tax SaaS; others exempt it. Product taxability is part of the nexus study." },
    ],
    citations: [
      { label: "South Dakota v. Wayfair, Inc., 138 S. Ct. 2080 (2018)", url: "https://www.supremecourt.gov/opinions/17pdf/17-494_j4el.pdf" },
      { label: "Multistate Tax Commission VDA", url: "https://www.mtc.gov/Nexus-Program/National-Nexus-Program-NNP" },
    ],
  },

  "tax-preparation": {
    quickAnswerBullets: [
      "CPA-prepared business returns: 1120 (C-Corp), 1120-S (S-Corp), 1065 (Partnership / LLC).",
      "Delinquent returns prepared from transcripts when records are missing.",
      "Amended returns (1120-X, 1120-S amended, 1065-X) to correct prior errors or apply audit results.",
    ],
    urgency: {
      headline: "Compliance is the foundation of every resolution — and the bedrock of audit defense.",
      points: [
        "Resolution work cannot proceed until current-year returns are filed.",
        "Each delinquent business return blocks personal resolution for owners (K-1 flows).",
        "Late-filed S-Corp and partnership returns carry $235/month per partner/shareholder penalties.",
      ],
    },
    stats: [
      { value: "$235/mo", label: "Late-filing penalty per shareholder (Form 1120-S)", source: "IRC § 6699" },
      { value: "$235/mo", label: "Late-filing penalty per partner (Form 1065)", source: "IRC § 6698" },
      { value: "12 mo", label: "Maximum months penalty accrues", source: "IRC § 6698/6699" },
    ],
    howItWorks: [
      { title: "Scope & reconstruction", body: "Identify every unfiled period; reconstruct income and expense from bank statements, prior returns, and transcripts." },
      { title: "Preparation & review", body: "CPA-prepared, partner-reviewed returns built to defend on audit. Schedules and elections documented." },
      { title: "Filing & resolution setup", body: "Returns filed; resulting balances rolled into the appropriate resolution (IA, OIC, CNC)." },
      { title: "Ongoing compliance", body: "Quarterly estimates, payroll-tax deposit calendars, and annual filing handled going forward." },
    ],
    disqualifiers: [
      "Returns are simple, current, and self-prepared successfully — DIY may be the right choice.",
      "You need tax planning beyond compliance (M&A, equity comp) — refer for specialized advisory.",
    ],
    timeline: [
      { phase: "Scope", duration: "1 week", what: "Returns identified; engagement letter signed." },
      { phase: "Preparation", duration: "2–8 weeks", what: "Reconstruction and prep complete." },
      { phase: "Filing & follow-up", duration: "1–2 weeks", what: "E-filed where eligible; paper filings tracked." },
    ],
    costFactors: [
      "Entity type (1120-S typically less than 1120 or consolidated 1120).",
      "Number of states.",
      "Whether prior returns must be reconstructed from transcripts.",
    ],
    mistakes: [
      { title: "Filing late returns one at a time", body: "We file the full delinquency window together to avoid each filing triggering separate CP-series escalation." },
      { title: "Failing to make penalty-abatement requests at filing", body: "Late-filing penalties are often abatable via FTA or reasonable cause — we request at the same time we file." },
    ],
    alternatives: [
      { name: "Tax software for simple S-Corp with no resolution exposure", whenItFits: "Single-shareholder S-Corp, clean books, no IRS exposure." },
    ],
    whyCTR: defaultWhyCTR,
    extendedFaqs: [
      { q: "Can you prepare returns when I have no records?", a: "Yes — we rebuild from IRS Wage & Income transcripts, bank statements, and prior returns. Most cases produce defensible returns even without original records." },
      { q: "Will filing late returns trigger an audit?", a: "Late filing alone is not an audit trigger. The audit risk comes from the content of the return — accuracy and consistency matter far more than timeliness." },
    ],
    citations: [
      { label: "IRC § 6698 — Failure to file partnership return", url: "https://www.law.cornell.edu/uscode/text/26/6698" },
      { label: "IRC § 6699 — Failure to file S corporation return", url: "https://www.law.cornell.edu/uscode/text/26/6699" },
    ],
  },
};

const defaultRich: ServiceRichContent = {
  whyCTR: defaultWhyCTR,
  citations: defaultCitations,
};

export function getServiceRich(serviceSlug: string): ServiceRichContent {
  const override = serviceRichContent[serviceSlug];
  if (!override) return defaultRich;
  return {
    ...override,
    whyCTR: override.whyCTR ?? defaultWhyCTR,
    citations: override.citations ?? defaultCitations,
  };
}
